import { isValid, parse } from 'date-fns'
import moment from 'moment'

export const validateDate = (
    value: string | null | undefined,
    dateFormat: string
) => {
    value = value || ''
    return value === '' ? true : isValid(parse(value, dateFormat, new Date()))
}

export const isDateRangeValid = (
    firstDate: string,
    secondDate: string,
    dateFormat: string,
    isReversed?: boolean
) => {
    const startDate = parse(firstDate, dateFormat, new Date())
    const endDate = parse(secondDate, dateFormat, new Date())
    if (isValid(startDate) && isValid(endDate))
        return !isReversed ? endDate >= startDate : startDate >= endDate
    return true
}

export const checkDate = (
    firstDate: string | null | undefined,
    secondDate: string,
    dateFormat: string,
    isReversed?: boolean
) => {
    return !(
        validateDate(firstDate, dateFormat) &&
        isDateRangeValid(
            firstDate as string,
            secondDate,
            dateFormat,
            isReversed
        )
    )
}

/**
 * Converts and formats a date string or Date object to a local date and time string.
 *
 * @param dateString - The date string or Date object to convert and format.
 * @param inputFormat - The format to use for parsing (string input) or formatting (Date input). Defaults to 'MMM DD, yyyy - hh:mm A'.
 * @returns The formatted local date and time string in the specified format, or 'Invalid date' if invalid.
 */
export const formatLocalDateTime = (
    dateString: string | Date | null,
    inputFormat: string = 'MMM DD, yyyy - hh:mm A'
): string => {
    if (!dateString) return 'Invalid date'

    // Parse the dateString if it's a string; otherwise, use it directly
    const momentDate =
        typeof dateString === 'string'
            ? moment.utc(dateString, inputFormat, true)
            : moment(dateString).utc()

    // Check if the parsed date is valid
    if (!momentDate.isValid()) {
        return 'Invalid date'
    }

    // Convert to local time and format the date
    return momentDate.local().format(inputFormat)
}

// Converts a Date object to a string in the local "YYYY-MM-DD" format
export const formatLocalDate = (date: Date | null): string | null => {
    if (!date) return null // Return null if no date is provided

    // Manually extract local year, month, and day from the Date object
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    // Format and return as a string "YYYY-MM-DD" in local time zone
    return `${year}-${month}-${day}`
}

//Normalize date string by adding 'T00:00:00' and handling potential splits
export const formatDateWithTimezone = (dateString: string) => {
    if (!dateString) return null

    // If the date string contains 'T', split it and append 'T00:00:00'
    if (dateString.includes('T')) {
        return dateString.split('T')[0] + 'T00:00:00' // Return in the format YYYY-MM-DDT00:00:00
    }

    // If it's just a date string (YYYY-MM-DD), append 'T00:00:00'
    return dateString + 'T00:00:00'
}
